import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';

/**
 * Context voor het radiale poort-paneel. Bevat naast de poort ook de
 * planeet-activaties van die poort: elke activatie draagt `line`, `color`,
 * `tone` én `base` en wijst daarmee exact één van de 1080 eindpunten aan, dus
 * de chart-highlight werkt zonder extra datastroom.
 *
 * Zie `docs/superpowers/specs/2026-07-30-hd-gate-radial-graph-design.md`.
 */
export interface HdGateGraphContext {
  /** Discriminator, zodat deze context te onderscheiden is van de gate-info contexten. */
  readonly kind: 'gate-graph';
  readonly gate: number;
  /**
   * Planeet-activaties van déze poort in de huidige chart. Mag leeg zijn: een
   * poort zonder activatie (of een klik buiten chart-context) toont gewoon de
   * volledige tekening zonder highlighting.
   */
  readonly activations: readonly HdBodygraphPlanetaryActivation[];
}

/** Dedupe-key van het paneel; een tweede klik op dezelfde poort focust i.p.v. dupliceert. */
export function buildHdGateGraphPanelKey(gate: number): string {
  return `gate-graph:${gate}`;
}

/** 0 = poort, 1 = lijn, 2 = kleur, 3 = toon, 4 = basis. */
export type HdGateGraphLayer = 0 | 1 | 2 | 3 | 4;

/**
 * Eén cirkel in de radiale boom.
 *
 * `id` is stabiel en menselijk leesbaar (`'g'`, `'g.2'`, `'g.2.4'`,
 * `'g.2.4.3'`, `'g.2.4.3.5'`) zodat hit-testing, de focus-overlay en een
 * eventuele latere cross-highlighting eraan kunnen haken zonder objectidentiteit
 * te moeten bewaren.
 */
export interface HdGateGraphNode {
  readonly id: string;
  readonly layer: HdGateGraphLayer;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  /** Richting ouder → deze knoop in graden; 0 = rechts, −90 = boven. */
  readonly direction: number;
  /** 1-gebaseerd nummer binnen de ouder; ontbreekt op de poort zelf. */
  readonly num?: number;
  readonly parent?: HdGateGraphNode;
  readonly children: readonly HdGateGraphNode[];
}

export interface HdGateGraphLayout {
  readonly root: HdGateGraphNode;
  /** Alle knopen, breedte-eerst; handig voor hit-testing en metingen. */
  readonly nodes: readonly HdGateGraphNode[];
  /** Halve zijde van de vierkante viewBox, marge inbegrepen. */
  readonly extent: number;
}
